import {useCallback, useEffect, useRef, useSyncExternalStore} from 'react'

import {cookieStore, type CookieWriteOptions} from './cookie-storage-store'

const {cache, read, write, reset, subscribe} = cookieStore

/**
 * Cookie-persisted state with cross-component sync, built on
 * `useSyncExternalStore` so it's hydration-safe. Unlike local/sessionStorage,
 * cookies are sent on every request, so the server can render markup matching
 * the client's first paint — seed `initialValue` from the request cookie during
 * SSR to avoid a flash.
 */
export function useCookieStorage<T>(
  key: string,
  initialValue: T,
  options?: CookieWriteOptions,
): [T, (value: T | ((prev: T) => T)) => void] {
  const initialValueRef = useRef(initialValue)
  useEffect(() => {
    initialValueRef.current = initialValue
  })

  // Keep options in a ref so `setValue`'s identity depends only on `key`, even
  // when callers pass an inline options literal.
  const optionsRef = useRef(options)
  useEffect(() => {
    optionsRef.current = options
  })

  const getSnapshot = useCallback((): T => {
    if (!cache.has(key)) {
      read(key, initialValue)
    }
    return cache.get(key) as T
  }, [key, initialValue])

  const getServerSnapshot = useCallback((): T => initialValue, [initialValue])

  const subscribeToStore = useCallback((listener: () => void) => subscribe(key, listener), [key])

  const value = useSyncExternalStore(subscribeToStore, getSnapshot, getServerSnapshot)

  const setValue = useCallback(
    (next: T | ((prev: T) => T)) => {
      const current = cache.has(key) ? (cache.get(key) as T) : initialValueRef.current
      const resolved = typeof next === 'function' ? (next as (prev: T) => T)(current) : next
      // Treat undefined as a reset, never persist the literal "undefined".
      if (resolved === undefined) {
        reset(key, initialValueRef.current)
      } else {
        write(key, resolved, optionsRef.current)
      }
    },
    [key],
  )

  return [value, setValue]
}
